import { v4 } from 'uuid';
import { ContentSourceType } from '@dtvgo/backend-service';

var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
class PlaybackHeartbeatController {
  /**
   * Creates a new instance of the class.
   * @constructor
   * @param options - The options object.
   * @param options.backendService - The backend service.
   * @param options.heartbeatTimeInSeconds - The heartbeat time in seconds.
   * @param options.deviceId - The device ID.
   * @param options.getStreamedTime - A function to get the streamed time.
   * @param options.getStreamedContentTime - A function to get the streamed content time.
   * @param options.useStreamingV4 - Indicates if the streaming V4 should be used.
   */
  constructor({
    backendService,
    heartbeatTimeInSeconds,
    deviceId,
    getStreamedTime,
    getStreamedContentTime,
    useStreamingV4,
    useStopV4
  }) {
    __publicField(this, "heartbeatTimeMilliseconds");
    __publicField(this, "deviceId");
    __publicField(this, "getStreamedTime");
    __publicField(this, "getStreamedContentTime");
    __publicField(this, "backendService");
    __publicField(this, "useStreamingV4");
    __publicField(this, "useStopV4", false);
    __publicField(this, "isStreamingActionSent", false);
    __publicField(this, "heartbeatIntervalId");
    __publicField(this, "sessionId");
    __publicField(this, "playbackId");
    __publicField(this, "progress");
    __publicField(this, "duration");
    __publicField(this, "content");
    __publicField(this, "stopPromise");
    __publicField(this, "programCompletionPromise");
    this.heartbeatTimeMilliseconds = heartbeatTimeInSeconds * 1e3;
    this.deviceId = deviceId;
    this.getStreamedTime = getStreamedTime;
    this.getStreamedContentTime = getStreamedContentTime;
    this.backendService = backendService;
    this.useStreamingV4 = useStreamingV4;
    this.useStopV4 = useStopV4;
  }
  /**
   * Starts the playback heartbeat with the given parameters.
   *
   * @param {object} options - The options for starting the playback.
   * @param {string} options.playbackId - The playback ID.
   * @param {Asset} options.asset - The asset being played.
   * @param {Schedule} [options.schedule] - The schedule for the playback.
   * @param {boolean} options.isLive - Indicates if the playback is live.
   */
  async start({
    playbackId,
    asset,
    schedule,
    isLive
  }) {
    if (this.playbackId && this.playbackId !== playbackId) {
      await this.stop();
    }
    if (this.playbackId === playbackId && this.heartbeatIntervalId)
      return;
    this.setContent({ asset, schedule, isLive });
    this.playbackId = playbackId;
    this.sessionId = v4();
    this.heartbeatIntervalId = setInterval(() => {
      this.sendStreaming().catch((error) => {
        console.error("[PlaybackHeartbeatController] heartbeat interval > error in send", error);
      });
    }, this.heartbeatTimeMilliseconds);
  }
  /**
   * Send the streaming request to the backend service
   */
  async streaming({
    asset,
    schedule,
    isLive
  }) {
    this.setContent({ asset, schedule, isLive });
    this.sessionId = v4();
    this.sendStreaming().catch((error) => {
      console.error("[PlaybackHeartbeatController] streaming > error in sendStreaming", error);
    });
  }
  /**
   * Stops the heartbeat and send the stop request to the backend service
   * @returns A Promise that resolves when the heartbeat is stopped.
   */
  async stop() {
    if (this.heartbeatIntervalId) {
      clearInterval(this.heartbeatIntervalId);
      this.heartbeatIntervalId = void 0;
    }
    if (this.playbackId && this.sessionId && this.content) {
      this.isStreamingActionSent = false;
      this.stopPromise = this.sendStop();
      this.playbackId = void 0;
      this.sessionId = void 0;
      this.content = void 0;
      return this.stopPromise.finally(() => {
        this.stopPromise = void 0;
      });
    }
    if (this.stopPromise !== void 0)
      return this.stopPromise;
    return void 0;
  }
  /**
   * When the live program ends. Send the programCompletion request to the backend service
   * @returns A Promise that resolves when the program is completion.
   */
  async programCompletion() {
    if (this.playbackId && this.sessionId && this.content) {
      this.programCompletionPromise = this.sendProgramCompletion();
      return this.programCompletionPromise.finally(() => {
        this.programCompletionPromise = void 0;
      });
    }
    if (this.programCompletionPromise !== void 0)
      return this.programCompletionPromise;
    return void 0;
  }
  /**
   * Sets the asset and the current schedule for the heartbeat.
   *
   * @param content - The content to set.
   * @param content.asset - The asset to set.
   * @param content.schedule - The schedule to set.
   * @param content.isLive - Indicates if the content is live.
   */
  setContent({ asset, schedule, isLive }) {
    const { vmsId, vrioAssetId, duration } = asset || {};
    const { guideId, startTime, endTime } = schedule || {};
    const durationTime = endTime && startTime && Math.floor((endTime.getTime() - startTime.getTime()) / 1e3);
    this.content = {
      vmsId,
      vrioAssetId,
      duration: isLive ? durationTime : duration,
      isLive,
      guideId
    };
  }
  updateProgress(progress) {
    this.progress = progress;
  }
  updateDuration(duration) {
    this.duration = duration;
  }
  getDuration() {
    return Math.ceil(
      this.content?.isLive ? this.content.duration || 0 : this.duration || this.content?.duration || 0
    ) || 0;
  }
  /**
   * Returns the payload used in the requests to the backend service.
   */
  getPayload() {
    return {
      contentType: this.content.isLive ? ContentSourceType.LIVE : ContentSourceType.VOD,
      device: this.deviceId,
      duration: this.getDuration(),
      sessionId: this.sessionId,
      progress: Math.floor(this.progress || 0),
      streamed: Math.floor(this.getStreamedTime() || 0),
      streamedContent: Math.floor(this.getStreamedContentTime() || 0),
      vmsId: this.content?.vmsId,
      guideId: this.content?.guideId,
      vrioAssetId: this.content?.vrioAssetId
    };
  }
  getPayloadV4() {
    return {
      ...this.getPayload(),
      isStreamingActionSent: this.isStreamingActionSent
    };
  }
  async sendStreaming() {
    if (this.useStreamingV4) {
      const response = await this.backendService.experience.v4.playbackStreaming(
        this.getPayloadV4()
      );
      this.isStreamingActionSent = response.data.isStreamingActionSent;
      return response;
    }
    return this.backendService.experience.v3.playbackStreaming(this.getPayload());
  }
  async sendStop() {
    if (this.useStopV4) {
      return this.backendService.experience.v4.playbackStop(this.getPayload()).catch((error) => {
        console.error("[PlaybackHeartbeatController] sendStop", error);
      });
    }
    return this.backendService.experience.v3.playbackStop(this.getPayload()).catch((error) => {
      console.error("[PlaybackHeartbeatController] sendStop", error);
    });
  }
  async sendProgramCompletion() {
    return this.backendService.experience.v4.playbackProgramCompletion(this.getPayload()).catch((error) => {
      console.error("[PlaybackHeartbeatController] sendProgramCompletion", error);
    });
  }
}

export { PlaybackHeartbeatController };
//# sourceMappingURL=PlaybackHeartbeatController.js.map
